import {TypeImplementation, withSystem, EnvTypes} from '@packages/systems/wfdl';
import {a, functionOf, TGeneric, TNumber} from '@packages/systems/wfdl-ast';
import {qualify, iconName} from '@packages/systems/wfdl-Qualified';
import {pluginName} from '../constants';
import {type DynamoEnvValueType} from '../types';
import {TOption} from '@packages/systems/plugins/Option';
import {
  toOperandExpr,
  TOperand,
} from '@packages/systems/plugins/ConditionsOperand';
import {
  TRUE,
  FALSE,
  TPredicate,
  BooleanTag,
  toBooleanTag,
} from '@packages/systems/plugins-utils/Boolean';
import {
  RefConditionValue,
  toListOfIds,
  TRefAnyOfConditionValue,
} from '../dataTypes/RefAnyOfConditionValue';
import {TDynamoItemRefSet} from '@packages/systems/plugins-utils/DynamoGateway';

export const ItemRefSetType = TypeImplementation.Custom({
  params: ['referencedCollectionId'],
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(pluginName, iconName('ItemRefSet'))
  ),
});

export const dynamoItemRefSetCondition: EnvTypes.TypedValue = {
  envType: {
    dataType: TGeneric({
      arg: 'a',
      body: functionOf(
        TOption(TDynamoItemRefSet(a)),
        TOperand(TOption(TDynamoItemRefSet(a)))
      ),
    }),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  expression: toOperandExpr,
};

export const dynamoItemRefSetContainsAny: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'contains any'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          TRefAnyOfConditionValue(a),
          TPredicate(TOption(TDynamoItemRefSet(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value:
    (refs: RefConditionValue[]) =>
    (x: DynamoEnvValueType<unknown>): BooleanTag => {
      if (x === null || x.tag === 'None') {
        return FALSE;
      }

      const itemRefSetValue = x.val;
      if (!Array.isArray(itemRefSetValue)) {
        return FALSE;
      }

      const refsSet = new Set(toListOfIds(refs));
      return toBooleanTag(
        itemRefSetValue.some(({id: itemRefId}) => refsSet.has(itemRefId))
      );
    },
};

export const dynamoItemRefSetDoesNotContainAny: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'does not contain any'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          TRefAnyOfConditionValue(a),
          TPredicate(TOption(TDynamoItemRefSet(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value:
    (refs: RefConditionValue[]) =>
    (x: DynamoEnvValueType<unknown>): BooleanTag => {
      if (x === null || x.tag === 'None') {
        return TRUE;
      }

      const itemRefSetValue = x.val;
      if (!Array.isArray(itemRefSetValue)) {
        return TRUE;
      }

      const refsSet = new Set(toListOfIds(refs));
      return toBooleanTag(
        !itemRefSetValue.some(({id: itemRefId}) => refsSet.has(itemRefId))
      );
    },
};

export const dynamoItemRefSetIsSet: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is set'},
      TGeneric({
        arg: 'a',
        body: TPredicate(TOption(TDynamoItemRefSet(a))),
      })
    ),
    exported: 'visible',
  },
  value: (x: DynamoEnvValueType<unknown>): BooleanTag => {
    if (x === null || x.tag === 'None' || !Array.isArray(x.val)) {
      return FALSE;
    }
    return toBooleanTag(x.val.length > 0);
  },
};

export const dynamoItemRefSetIsNotSet: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is not set'},
      TGeneric({
        arg: 'a',
        body: TPredicate(TOption(TDynamoItemRefSet(a))),
      })
    ),
    exported: 'visible',
  },
  value: (x: DynamoEnvValueType<unknown>): BooleanTag => {
    if (x === null || x.tag === 'None' || !Array.isArray(x.val)) {
      return TRUE;
    }
    return toBooleanTag(x.val.length === 0);
  },
};

export const dynamoItemRefSetCountEquals: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'count is'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          withSystem({isBindable: true}, TNumber),
          TPredicate(TOption(TDynamoItemRefSet(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value:
    (num: number) =>
    (x: DynamoEnvValueType<unknown>): BooleanTag => {
      return toBooleanTag(getCount(x) === num);
    },
};

export const dynamoItemRefSetCountDoesNotEqual: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'count is not'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          withSystem({isBindable: true}, TNumber),
          TPredicate(TOption(TDynamoItemRefSet(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value:
    (num: number) =>
    (x: DynamoEnvValueType<unknown>): BooleanTag => {
      return toBooleanTag(getCount(x) !== num);
    },
};

export const dynamoItemRefSetCountIsGreaterThan: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'count is greater than'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          withSystem({isBindable: true}, TNumber),
          TPredicate(TOption(TDynamoItemRefSet(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value:
    (num: number) =>
    (x: DynamoEnvValueType<unknown>): BooleanTag => {
      return toBooleanTag(getCount(x) > num);
    },
};

export const dynamoItemRefSetCountIsGreaterThanEqualTo: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'count is greater or equal'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          withSystem({isBindable: true}, TNumber),
          TPredicate(TOption(TDynamoItemRefSet(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value:
    (num: number) =>
    (x: DynamoEnvValueType<unknown>): BooleanTag => {
      return toBooleanTag(getCount(x) >= num);
    },
};

export const dynamoItemRefSetCountIsLessThan: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'count is less than'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          withSystem({isBindable: true}, TNumber),
          TPredicate(TOption(TDynamoItemRefSet(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value:
    (num: number) =>
    (x: DynamoEnvValueType<unknown>): BooleanTag => {
      return toBooleanTag(getCount(x) < num);
    },
};

export const dynamoItemRefSetCountIsLessThanEqualTo: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'count is less or equal'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          withSystem({isBindable: true}, TNumber),
          TPredicate(TOption(TDynamoItemRefSet(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value:
    (num: number) =>
    (x: DynamoEnvValueType<unknown>): BooleanTag => {
      return toBooleanTag(getCount(x) <= num);
    },
};

function getCount(dynamoItemRefSetOption: DynamoEnvValueType<unknown>): number {
  if (
    dynamoItemRefSetOption === null ||
    dynamoItemRefSetOption.tag === 'None' ||
    !Array.isArray(dynamoItemRefSetOption.val)
  ) {
    return 0;
  }
  return dynamoItemRefSetOption.val.length;
}
